import AbstractAccountGroup from "./AbstractAccountGroup.js";

/**
 * `accountGroupAliasing` is the class of account group aliases.
 * Aliases are used in the CoA secondary tree to link account group ids,
 * that are needed for KPI calculation, to the equivalent account groups
 * in the CoA main tree that have different ids.
 *
 * @param {accountGroup[]} spec.aliasedAccountGroup - Wrapped account group
 */
export default class AccountGroupAliasing extends AbstractAccountGroup {
	constructor({ aliasedAccountGroup } = {}) {
		super(...arguments);

		this._aliasedAccountGroup = aliasedAccountGroup;
	}

	getAliasedAccountGroup() {
		return this._aliasedAccountGroup;
	}

	getChildren() {
		return this._aliasedAccountGroup.getChildren();
	}

	findById() {
		return (
			super.findById(...arguments) ||
			this._aliasedAccountGroup.findById(...arguments)
		);
	}

	matchString(string) {
		return (
			super.matchString(string) ||
			this._aliasedAccountGroup.matchString(string)
		);
	}

	asSpecification() {
		return this._aliasedAccountGroup.asSpecification();
	}

	hasAccounts() {
		return this._aliasedAccountGroup.hasAccounts();
	}

	hasAccount(account) {
		return this._aliasedAccountGroup.hasAccount(account);
	}

	canBeAddedTo(group) {
		// Aliases can only be added to the secondary tree
		return group === this._chartOfAccounts.getSecondaryTree();
	}

	representsGroup(group) {
		return (
			super.representsGroup(...arguments) || this.isAliasingGroup(group)
		);
	}

	isAliasingGroup(group) {
		return group.getId() === this._aliasedAccountGroup.getId();
	}

	accept(visitor) {
		return visitor.visitAccountGroupAliasing(this);
	}

	isChildOf(parentAccountGroupId) {
		return this._chartOfAccounts.isChildOf({
			parentAccountGroupId,
			childAccountGroupId: this.getAliasedAccountGroup().getId(),
		});
	}

	_getName() {
		return this._aliasedAccountGroup.getName();
	}

	_serializedProperties() {
		const schema = super._serializedProperties();

		schema.aliasedGroupId =
			this._aliasedAccountGroup && this._aliasedAccountGroup.getId();

		delete schema.aliasedAccountGroup;
		return schema;
	}

	static getType() {
		return "AccountGroupAliasing";
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.aliasedAccountGroup = context?.mainTree?.findById(
			data.aliasedGroupId,
		);
	}
}
